import {
  BaseEquipment,
  CardioOrResistance,
  Equipment,
  EquipmentRequiringInstruction,
  GymTypes,
  SmallBodyGroupType,
} from 'fitbeat.models';
import { Exercise, ExerciseFormat, WorkoutSettings } from '../../entities';
import { findClosesValueInArray } from '../../helpers/math';
import { BandResistanceStrategy } from './BandResistanceStrategy';
import {
  homeEquipmentWeightInfoMapping,
  relativeResistanceForHomeEquipment,
  relativeResistanceForStudioEquipment,
  studioEquipmentWeightInfoMapping,
} from './equimpentWeightMaps';
import {
  EquipmentWeightCalculator,
  IResistanceStrategy,
  IUserInfoForEquipmentWeight,
  IWeightAndColor,
} from './EquipmentWeightCalculator';

export const getEquipmentResistanceStrategy = (
  gymType: GymTypes,
  primaryEquipment: EquipmentRequiringInstruction,
) => {
  const isHomeGym = gymType === GymTypes.Home;
  if (BaseEquipment.isBand(primaryEquipment)) {
    return new BandResistanceStrategy(isHomeGym, primaryEquipment);
  }
  return new EquipmentResistanceStrategy(isHomeGym);
};

export class EquipmentResistanceStrategy implements IResistanceStrategy {
  private equipmentWeightInfoMap: Map<
    EquipmentRequiringInstruction,
    Map<number, IWeightAndColor>
  >;
  private resistanceMap: Map<Equipment, Map<number, number>>;
  private ratioResistanceEquation: (resistance: number, ratio: number) => number;

  constructor(private isHomeGym: boolean) {
    this.equipmentWeightInfoMap = this.isHomeGym
      ? homeEquipmentWeightInfoMapping
      : studioEquipmentWeightInfoMapping;
    this.resistanceMap = this.isHomeGym
      ? relativeResistanceForHomeEquipment
      : relativeResistanceForStudioEquipment;
    this.ratioResistanceEquation = (resistance: number, ratio: number) => {
      return resistance * ratio;
    };
  }
  public getEquipmentWeight(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    primaryMover: SmallBodyGroupType,
    user: IUserInfoForEquipmentWeight,
  ): IWeightAndColor {
    const equipmentWeightMap = this.equipmentWeightInfoMap.get(equipment);
    const relativeResistanceMap = this.getRelativeResistanceMap(equipment);
    if (!equipmentWeightMap) {
      throw new Error(`No equipment weigth mapping for, ${equipment}`);
    }
    const mappedExerciseResistance =
      relativeResistanceMap.get(exerciseResistance);
    if (!mappedExerciseResistance) {
      throw new Error(
        `Incorrect exercise resistnace value set for exercise ${uniqueReferenceNumber}`,
      );
    }
    const strengthResistanceForUser =
      EquipmentWeightCalculator.getStrengthResistanceForUser(
        mappedExerciseResistance,
        primaryMover,
        user,
        uniqueReferenceNumber,
        this.ratioResistanceEquation,
      );
    const roundedExerciseResistance = this.getRoundedValue(
      strengthResistanceForUser,
      relativeResistanceMap,
    );
    const equipmentWeight = equipmentWeightMap.get(roundedExerciseResistance);
    if (!equipmentWeight) {
      throw new Error(
        `Could not get equipment weight from adjusted resistance for
        ${equipment} and exercise ${uniqueReferenceNumber}`,
      );
    }
    return equipmentWeight;
  }
  public getEquipmentWeightAdjustedForCardioOrResistance(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    exerciseFormats: ExerciseFormat[],
    primaryMover: SmallBodyGroupType,
    settings: WorkoutSettings,
    user: IUserInfoForEquipmentWeight,
  ) {
    const equipmentWeightMap = this.getEquipmentWeightMap(equipment);
    const relativeResistanceMap = this.getRelativeResistanceMap(equipment);
    if (!exerciseResistance) {
      throw new Error(
        `No exercise resistnace set for exercise ${uniqueReferenceNumber}`,
      );
    }
    const mappedExerciseResistance =
      relativeResistanceMap.get(exerciseResistance);
    if (!mappedExerciseResistance) {
      throw new Error(
        `Incorrect exercise resistnace value set for exercise ${uniqueReferenceNumber}`,
      );
    }
    const strengthResistanceForUser =
      EquipmentWeightCalculator.getStrengthResistanceForUser(
        mappedExerciseResistance,
        primaryMover,
        user,
        uniqueReferenceNumber,
        this.ratioResistanceEquation,
      );
    const relativeResistancevalues = [...relativeResistanceMap.values()];

    let roundedExerciseResistance = this.getRoundedValue(
      strengthResistanceForUser,
      relativeResistanceMap,
    );
    const shouldLowerWeight =
      Exercise.hasBuildAndBurnFormats(exerciseFormats) &&
      settings.cardioOrResistance === CardioOrResistance.Cardio;
    if (shouldLowerWeight) {
      const indexOfRelativeValue = relativeResistancevalues.findIndex(
        (value) => value === roundedExerciseResistance,
      );
      roundedExerciseResistance =
        relativeResistancevalues[Math.max(indexOfRelativeValue - 1, 0)];
    }
    const equipmentWeight = equipmentWeightMap.get(roundedExerciseResistance);
    if (!equipmentWeight) {
      throw new Error(
        `Could not get equipment weight from relative resistance for
        ${equipment} and exercise ${uniqueReferenceNumber}`,
      );
    }
    return {
      equipmentWeight,
      adjustedExerciseResistance: roundedExerciseResistance,
    };
  }
  public calculateUserStrengthRatio(
    userWeight: number,
    exerciseWeight: number,
    equipmentRequiringInstruction?: Equipment,
  ) {
    if (!equipmentRequiringInstruction) {
      throw new Error(`Equipment must be defined to calculate user's strength ratio for dumbbell assessment`);
    }
    const resistanceMap = this.resistanceMap.get(equipmentRequiringInstruction);
    if (!resistanceMap) {
      throw new Error(`Expected resistance map to be defined for ${equipmentRequiringInstruction}`);
    }
    const exerciseRelativeResistance = resistanceMap.get(exerciseWeight);
    const exerciseRelativeResistanceForUser = resistanceMap.get(userWeight);
    if(!exerciseRelativeResistance) {
      throw new Error(`no relative resistance for ${exerciseWeight}kg ${equipmentRequiringInstruction}`);
    }
    if(!exerciseRelativeResistanceForUser) {
      throw new Error(`no relative resistance for a ${userWeight}kg ${equipmentRequiringInstruction}`);
    }
    return exerciseRelativeResistanceForUser/exerciseRelativeResistance;
  }
  private getRoundedValue(
    exerciseResistance: number,
    relativeResistanceMap: Map<number, number>,
  ) {
    const relativeResistancevalues = [...relativeResistanceMap.values()];
    const roundedValue = findClosesValueInArray(
      relativeResistancevalues,
      exerciseResistance,
    );
    return roundedValue;
  }
  private getEquipmentWeightMap(equipment: EquipmentRequiringInstruction) {
    const equipmentWeightMap = this.equipmentWeightInfoMap.get(equipment);
    if (!equipmentWeightMap) {
      throw new Error(`No mapping for, ${equipment}`);
    }
    return equipmentWeightMap;
  }
  private getRelativeResistanceMap(equipment: EquipmentRequiringInstruction) {
    const equipmentWeightMap = this.resistanceMap.get(equipment);
    if (!equipmentWeightMap) {
      throw new Error(`No relative resistance mapping for, ${equipment}`);
    }
    return equipmentWeightMap;
  }
}
